import { t } from "i18next";
import { ComponentProps } from "react";
import { ComputeFieldComponent } from "../computeFields";
import { MultipleSampleInBibliography } from "../types";
import { resolveBibliographieResumeLong } from "./resolveBibliographieResumeLong";
import { renderReproductionPart } from "./shared/bibliography-reproduction";
import { SeparatedList } from "./shared/utils";

export const BibliographyDescriptionMultiplesampleCF: ComputeFieldComponent<
  Pick<
    MultipleSampleInBibliography,
    | "bibliography"
    | "multiplesampleDansBibliographieDescriptionCitePage"
    | "multiplesampleDansBibliographieDescriptionReproduitPlanche"
    | "multiplesampleDansBibliographieDescriptionPlancheNonNumerotee"
    | "multiplesampleDansBibliographieDescriptionListePage"
    | "multiplesampleDansBibliographieDescriptionSousNumero"
    | "multiplesampleDansBibliographieDescriptionReproductionCouleur"
    | "multiSampleInBiblioDescReproCover"
    | "multiplesampleDansBibliographieDescriptionNonPagine"
    | "multiplesampleDansBibliographieDescriptionTexteLibre"
    | "multiplesampleDansBibliographieDescriptionPhotographe"
    | "multiplesampleDansBibliographieDescriptionReproduitPage"
  >
> = ({ input, language, ...props }) => {
  const resumeBiblio = resolveBibliographieResumeLong(
    input.bibliography,
    language,
  );
  const texteLibre = (
    input.multiplesampleDansBibliographieDescriptionTexteLibre ?? ""
  ).trim();

  if (texteLibre) {
    return (
      <SeparatedList
        {...props}
        lineBreak={false}
        language={language}
        commaSeparated
        input={[
          <span dangerouslySetInnerHTML={{ __html: resumeBiblio }} />,
          texteLibre,
        ]}
      />
    );
  }

  const coul =
    !!input.multiplesampleDansBibliographieDescriptionReproductionCouleur;
  const couv = !!input.multiSampleInBiblioDescReproCover;
  const nP = !!input.multiplesampleDansBibliographieDescriptionNonPagine;
  const page = (
    input.multiplesampleDansBibliographieDescriptionReproduitPage ?? ""
  ).trim();
  const planche = (
    input.multiplesampleDansBibliographieDescriptionReproduitPlanche ?? ""
  ).trim();
  const plancheNonNum =
    !!input.multiplesampleDansBibliographieDescriptionPlancheNonNumerotee;
  const citePage = (
    input.multiplesampleDansBibliographieDescriptionCitePage ?? ""
  ).trim();
  const listePage = (
    input.multiplesampleDansBibliographieDescriptionListePage ?? ""
  ).trim();
  const sousNumero = (
    input.multiplesampleDansBibliographieDescriptionSousNumero ?? ""
  ).trim();

  const reproducePart = renderReproductionPart(
    { coul, couv, nP, page, planche, plancheNonNum },
    t,
  );

  const citePart = citePage && (
    <>
      {t("computeField.citePageAbbrege")} {citePage}
    </>
  );

  const listePart = listePage && (
    <>
      {t("computeField.listePageAbbrege")} {listePage}
      {sousNumero && (
        <>
          {" "}
          ({t("computeField.sousNumeroPrefix")}
          {sousNumero})
        </>
      )}
    </>
  );

  return (
    <SeparatedList
      {...props}
      lineBreak={false}
      language={language}
      commaSeparated
      input={[
        <span dangerouslySetInnerHTML={{ __html: resumeBiblio }} />,
        citePart,
        reproducePart,
        listePart,
      ]}
    />
  );
};

export const RelatedBibliographiesMultiplesamplesCF: ComputeFieldComponent<{
  relatedBibliographies?: ComponentProps<
    typeof BibliographyDescriptionMultiplesampleCF
  >["input"][];
}> = ({ input, lineBreak = true, ...props }) => {
  if (
    !input.relatedBibliographies ||
    input.relatedBibliographies.length === 0
  ) {
    return <></>;
  }
  return (
    <SeparatedList
      {...props}
      input={input.relatedBibliographies.map((bibliography) => (
        <BibliographyDescriptionMultiplesampleCF
          input={bibliography}
          {...props}
        />
      ))}
      lineBreak={lineBreak}
    />
  );
};
